import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import type { AppDispatch } from 'state/store';
import { selectActiveDefectLayers } from 'state/surveyDataManager';
import { mergeScannerMockCatalogue } from 'domain/surveyDataManager/api/mergeScannerMockCatalogue';
import { mergeDefectOverlayCatalogDefects } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayCatalogs';
import { mergeDefectOverlayViews } from 'domain/surveyDataManager/defectOverlays/mergeDefectOverlayViews';
import { useAvailableViews, useDefects } from 'domain/surveyDataManager/hooks';

import {
  SDM_SET_RECIPE_VISIBILITY_EVENT,
  type SdmSetRecipeVisibilityDetail,
} from './components/AvailableViewsPanel/sdmFavouriteKeys';
import { dispatchSdmPanToDefectExtent } from 'domain/surveyDataManager/map/sdmPanToDefectExtent';
import {
  activateRecipeView,
  claimMatchingRecipeOwnership,
  deactivateRecipeView,
} from './recipeVisibility';

/**
 * always-mounted bridge so Legend favourite checkboxes can activate/deactivate
 * SDM recipes even when the Survey Data Viewer modal is closed.
 * also claims recipe ownership for orphan map layers that already match a view.
 */
function SdmRecipeVisibilityBridge(): null {
  const dispatch = useDispatch<AppDispatch>();
  const queryClient = useQueryClient();
  const activeLayers = useSelector(selectActiveDefectLayers);
  const defectsQuery = useDefects({}, false);
  const availableViewsQuery = useAvailableViews(false);

  const catalogDefects = useMemo(
    () =>
      mergeDefectOverlayCatalogDefects(
        mergeScannerMockCatalogue(defectsQuery.data?.defects ?? []),
      ),
    [defectsQuery.data?.defects],
  );
  const availableViews = mergeDefectOverlayViews({
    views: availableViewsQuery.data?.views ?? [],
  }).views;

  const activeLayersRef = useRef(activeLayers);
  const catalogDefectsRef = useRef(catalogDefects);
  const availableViewsRef = useRef(availableViews);
  activeLayersRef.current = activeLayers;
  catalogDefectsRef.current = catalogDefects;
  availableViewsRef.current = availableViews;

  // claim ownership when orphans match a recipe (wizard / session / already-active).
  useEffect(() => {
    claimMatchingRecipeOwnership(
      dispatch,
      activeLayers,
      availableViews,
      catalogDefects,
    );
  }, [activeLayers, availableViews, catalogDefects, dispatch]);

  useEffect(() => {
    const onSetRecipeVisibility = (event: Event) => {
      const detail = (event as CustomEvent<SdmSetRecipeVisibilityDetail>).detail;
      if (!detail?.recipeId) {
        return;
      }
      const view = availableViewsRef.current.find(
        (entry) => entry.id === detail.recipeId,
      );
      if (!view) {
        return;
      }
      if (!detail.visible) {
        deactivateRecipeView({
          view,
          catalogDefects: catalogDefectsRef.current,
          dispatch,
        });
        return;
      }
      void activateRecipeView({
        view,
        catalogDefects: catalogDefectsRef.current,
        activeLayers: activeLayersRef.current,
        dispatch,
        queryClient,
      }).then(() => {
        dispatchSdmPanToDefectExtent(view.id);
      });
    };

    window.addEventListener(SDM_SET_RECIPE_VISIBILITY_EVENT, onSetRecipeVisibility);
    return () =>
      window.removeEventListener(
        SDM_SET_RECIPE_VISIBILITY_EVENT,
        onSetRecipeVisibility,
      );
  }, [dispatch, queryClient]);

  return null;
}

export default SdmRecipeVisibilityBridge;
